/**
 * See It In Homes Like Yours — image comparison + environment carousel.
 */
(function () {
  'use strict';

  var FADE_DURATION = 200;
  var MIN_LOOP_SLIDES = 8;

  /**
   * @param {Function} callback
   */
  function whenIdle(callback) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(callback, { timeout: 2000 });
      return;
    }

    setTimeout(callback, 500);
  }

  /**
   * @param {HTMLElement} root
   */
  function initImageComparison(root) {
    if (!root || root.dataset.comparisonInit === 'true') {
      return;
    }

    var beforeLayer = root.querySelector('[data-comparison-before]');
    var divider = root.querySelector('[data-comparison-divider]');
    var handle = root.querySelector('[data-comparison-handle]');

    if (!beforeLayer || !divider || !handle) {
      return;
    }

    root.dataset.comparisonInit = 'true';

    var position = 50;
    var isDragging = false;

    /**
     * @param {number} percent
     */
    function setPosition(percent) {
      position = Math.min(100, Math.max(0, percent));
      var rightClip = 100 - position;

      beforeLayer.style.clipPath = 'inset(0 ' + rightClip + '% 0 0)';
      divider.style.left = position + '%';
      handle.style.left = position + '%';
    }

    /**
     * @param {number} clientX
     */
    function updateFromClientX(clientX) {
      var rect = root.getBoundingClientRect();
      var percent = ((clientX - rect.left) / rect.width) * 100;
      setPosition(percent);
    }

    function onPointerDown(event) {
      isDragging = true;
      handle.setPointerCapture(event.pointerId);
      updateFromClientX(event.clientX);
      event.preventDefault();
      event.stopPropagation();
    }

    function onPointerMove(event) {
      if (!isDragging) {
        return;
      }

      updateFromClientX(event.clientX);
      event.stopPropagation();
    }

    function onPointerUp(event) {
      if (!isDragging) {
        return;
      }

      isDragging = false;

      if (handle.hasPointerCapture(event.pointerId)) {
        handle.releasePointerCapture(event.pointerId);
      }
    }

    handle.addEventListener('pointerdown', onPointerDown);
    handle.addEventListener('pointermove', onPointerMove);
    handle.addEventListener('pointerup', onPointerUp);
    handle.addEventListener('pointercancel', onPointerUp);

    setPosition(position);
  }

  /**
   * @param {HTMLElement} section
   */
  function updateContent(section, slideEl) {
    if (!slideEl) {
      return;
    }

    var titleEl = section.querySelector('[data-hly-title]');
    var descEl = section.querySelector('[data-hly-desc]');
    var roomSizeEl = section.querySelector('[data-hly-room-size]');
    var homeStyleEl = section.querySelector('[data-hly-home-style]');
    var sofaConfigEl = section.querySelector('[data-hly-sofa-config]');
    var interiorStyleEl = section.querySelector('[data-hly-interior-style]');

    if (titleEl) {
      titleEl.textContent = slideEl.dataset.title || '';
    }

    if (descEl) {
      descEl.textContent = slideEl.dataset.description || '';
    }

    if (roomSizeEl) {
      roomSizeEl.textContent = slideEl.dataset.roomSize || '';
    }

    if (homeStyleEl) {
      homeStyleEl.textContent = slideEl.dataset.homeStyle || '';
    }

    if (sofaConfigEl) {
      sofaConfigEl.textContent = slideEl.dataset.sofaConfig || '';
    }

    if (interiorStyleEl) {
      interiorStyleEl.textContent = slideEl.dataset.interiorStyle || '';
    }
  }

  /**
   * @param {HTMLElement} section
   * @param {number} activeIndex
   * @param {number} total
   */
  function updateCounter(section, activeIndex, total) {
    var counterEl = section.querySelector('[data-hly-counter]');
    if (!counterEl) {
      return;
    }

    var current = String(activeIndex + 1).padStart(2, '0');
    var count = String(total).padStart(2, '0');
    counterEl.textContent = current + ' / ' + count;
  }

  /**
   * Fades the content panel out, swaps its copy, then fades it back in.
   *
   * @param {HTMLElement} section
   * @param {HTMLElement} slideEl
   * @param {number} activeIndex
   * @param {number} total
   */
  function transitionContent(section, slideEl, activeIndex, total) {
    var wrap = section.querySelector('[data-hly-content-wrap]');

    if (!wrap) {
      updateContent(section, slideEl);
      updateCounter(section, activeIndex, total);
      return;
    }

    if (section.hlyFadeTimer) {
      clearTimeout(section.hlyFadeTimer);
    }

    wrap.classList.add('is-fading');

    section.hlyFadeTimer = setTimeout(function () {
      updateContent(section, slideEl);
      updateCounter(section, activeIndex, total);
      wrap.classList.remove('is-fading');
    }, FADE_DURATION);
  }

  /**
   * Loop mode moves slides between the two ends of the track, so a lazy image
   * that never entered the viewport shows as a blank peek. Promote them.
   *
   * @param {HTMLElement} slideEl
   */
  function eagerLoadSlideImages(slideEl) {
    if (!slideEl) {
      return;
    }

    slideEl.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      img.loading = 'eager';
    });
  }

  /**
   * @param {Object} swiper
   */
  function loadNeighbourImages(swiper) {
    for (var offset = -2; offset <= 2; offset += 1) {
      eagerLoadSlideImages(swiper.slides[swiper.activeIndex + offset]);
    }
  }

  /**
   * Swiper needs roughly twice the visible slides to fill both sides of a
   * loop. With few environments the peeks come up empty, so repeat the set.
   *
   * @param {HTMLElement} swiperEl
   * @param {HTMLElement[]} slides
   */
  function padSlidesForLoop(swiperEl, slides) {
    var wrapper = swiperEl.querySelector('.swiper-wrapper');
    if (!wrapper) {
      return;
    }

    wrapper.querySelectorAll('[data-hly-clone]').forEach(function (clone) {
      clone.remove();
    });

    if (slides.length >= MIN_LOOP_SLIDES) {
      return;
    }

    var passes = Math.ceil(MIN_LOOP_SLIDES / slides.length) - 1;

    for (var pass = 0; pass < passes; pass += 1) {
      slides.forEach(function (slide) {
        var clone = slide.cloneNode(true);
        clone.setAttribute('data-hly-clone', '');
        wrapper.appendChild(clone);
      });
    }
  }

  /**
   * @param {HTMLElement} section
   */
  function initSection(section) {
    if (!section || section.dataset.hlyInit === 'true') {
      return;
    }

    if (typeof Swiper === 'undefined') {
      return;
    }

    section.dataset.hlyInit = 'true';

    var swiperEl = section.querySelector('[data-hly-swiper]');
    var prevBtn = section.querySelector('[data-hly-prev]');
    var nextBtn = section.querySelector('[data-hly-next]');
    var slides = Array.prototype.slice.call(
      section.querySelectorAll('[data-hly-slide]'),
    );

    if (!swiperEl || !slides.length) {
      return;
    }

    if (swiperEl.swiper) {
      swiperEl.swiper.destroy(true, true);
    }

    var slideCount = slides.length;
    var canLoop = slideCount > 1;

    if (canLoop) {
      padSlidesForLoop(swiperEl, slides);
    }

    var swiperInstance = new Swiper(swiperEl, {
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 6,
      speed: 450,
      loop: canLoop,
      loopAdditionalSlides: slideCount,
      watchSlidesProgress: true,
      noSwipingClass: 'swiper-no-swiping',
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
      },
      on: {
        init: function (swiper) {
          var index = swiper.realIndex % slideCount;
          updateContent(section, slides[index]);
          updateCounter(section, index, slideCount);
          loadNeighbourImages(swiper);
        },
        slideChange: function (swiper) {
          var index = swiper.realIndex % slideCount;
          transitionContent(section, slides[index], index, slideCount);
          loadNeighbourImages(swiper);
        },
      },
    });

    swiperEl.querySelectorAll('[data-comparison]').forEach(initImageComparison);
    swiperEl.swiper = swiperInstance;

    whenIdle(function () {
      swiperEl.querySelectorAll('[data-hly-slide]').forEach(eagerLoadSlideImages);
    });
  }

  function initAll() {
    document.querySelectorAll('[data-homes-like-yours]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[data-homes-like-yours]');
    if (section) {
      section.dataset.hlyInit = 'false';
      section.querySelectorAll('[data-comparison]').forEach(function (el) {
        el.dataset.comparisonInit = 'false';
      });
      initSection(section);
    }
  });
})();
